import {
  type AgentDetail,
  AgentDetailSchema,
  type AgentPage,
  AgentPageSchema,
  type AgentSpec,
  type ListAgentsQuery,
} from '@sbc/domain-models/agents';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiGet, apiSend } from './client';

const AGENTS_KEY = ['admin', 'agents'];

function listKey(query: ListAgentsQuery) {
  return [...AGENTS_KEY, 'list', query] as const;
}

function detailKey(id: string) {
  return [...AGENTS_KEY, 'detail', id] as const;
}

export function useAgents(query: ListAgentsQuery) {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  if (query.search) {
    params.set('search', query.search);
  }
  return useQuery<AgentPage>({
    queryKey: listKey(query),
    queryFn: () => apiGet(`/api/admin/agents?${params.toString()}`, AgentPageSchema),
  });
}

export function useAgent(id: string, options: { enabled?: boolean } = {}) {
  return useQuery<AgentDetail>({
    queryKey: detailKey(id),
    queryFn: () => apiGet(`/api/admin/agents/${id}`, AgentDetailSchema),
    // Callers with no id yet (e.g. an unselected agent select) pass enabled:false
    // so we don't fire a doomed request to /api/admin/agents/.
    enabled: options.enabled ?? true,
  });
}

export function useCreateAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (spec: AgentSpec) => apiSend('POST', '/api/admin/agents', spec, AgentDetailSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: AGENTS_KEY }),
  });
}

export function useUpdateAgent(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (spec: AgentSpec) =>
      apiSend('PUT', `/api/admin/agents/${id}`, spec, AgentDetailSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: AGENTS_KEY }),
  });
}

export function useSetActiveVersion(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) =>
      apiSend('PUT', `/api/admin/agents/${id}/active-version`, { versionId }, AgentDetailSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: AGENTS_KEY }),
  });
}
